"use client"

import { useCallback, useEffect, useMemo, useRef } from "react"
import dynamic from "next/dynamic"

import { json, jsonParseLinter } from "@codemirror/lang-json"
import { linter, lintGutter, type Diagnostic } from "@codemirror/lint"
import { EditorView } from "@codemirror/view"
import { useTheme } from "next-themes"

// The editor view needs the DOM, so load it client-side only (mirrors how
// SqlEditor lazy-loads CodeMirror).
const CodeMirror = dynamic(() => import("@uiw/react-codemirror"), {
  ssr: false,
})

const parseJsonDocument = jsonParseLinter()

/**
 * Reports JSON parse errors inline. A blank document reports nothing, because
 * an empty optional field is not a syntax error the user needs to see.
 */
export function jsonDiagnostics(view: EditorView): Diagnostic[] {
  return view.state.doc.toString().trim() === "" ? [] : parseJsonDocument(view)
}

const jsonSyntaxLinter = linter(jsonDiagnostics)

// CodeMirror's bundled dark theme uses its own blue-gray canvas. Keep syntax
// colors theme-aware, but make the editor surface belong to the surrounding
// Coval UI in both light and dark mode.
const semanticSurfaceTheme = EditorView.theme({
  "&": {
    backgroundColor: "var(--surface-default) !important",
  },
  ".cm-gutters": {
    backgroundColor: "var(--surface-default) !important",
  },
  ".cm-activeLine, .cm-activeLineGutter": {
    backgroundColor: "var(--surface-1) !important",
  },
})

// CodeMirror renders a 14px line at a 1.4 line-height inside 4px of vertical
// content padding, which is what turns a `rows` count into a pixel floor.
const LINE_HEIGHT_PX = 20
const CONTENT_PADDING_PX = 8

const DEFAULT_ROWS = 4
const DEFAULT_MAX_HEIGHT = "320px"

/**
 * One of the two naming props is mandatory, mirroring `@coval/ui`'s `Textarea`,
 * so an unnamed editor fails typecheck rather than shipping an unlabeled
 * textbox to a screen reader.
 */
type AccessibleName =
  | { ariaLabel: string; ariaLabelledBy?: string }
  | { ariaLabel?: string; ariaLabelledBy: string }

interface JsonCodeEditorOwnProps {
  /** The JSON text. Raw text rather than a parsed object, so partially typed and templated values survive. */
  value: string
  /** Called with the new text on every edit. */
  onChange: (value: string) => void
  /** Called when the editor gains focus. */
  onFocus?: () => void
  /** Called when the editor loses focus, for consumers that reformat or commit on blur. */
  onBlur?: () => void
  /** Placeholder shown when the editor is empty. */
  placeholder?: string
  /** Fixed editor height. Omit to let the editor grow from `rows` up to `maxHeight`. */
  height?: string
  /** Lines shown before the editor starts growing (default: 4). Ignored when `height` is set. */
  rows?: number
  /** Height ceiling when `height` is omitted, past which the editor scrolls (default: "320px"). */
  maxHeight?: string
  /** Optional className for the container. */
  className?: string
  /** Id of an element describing the editor, typically the help text below it. */
  ariaDescribedBy?: string
  /** Whether to announce the field as required. */
  ariaRequired?: boolean
  /** Whether the editor is non-editable and dimmed. */
  disabled?: boolean
  /** Whether the editor is non-editable but still focusable and undimmed. */
  readOnly?: boolean
  /**
   * Strips the border, line numbers, and gutters, for editors that sit inline
   * in a dense surface such as a table cell alongside plain inputs.
   */
  chromeless?: boolean
  /**
   * Whether to underline JSON parse errors. Turn this off for fields that hold
   * template variables such as `{{agent.api_key}}`, which are not valid JSON on
   * their own; those fields validate through `error` instead.
   */
  lint?: boolean
  /** Validation message owned by the consumer. Renders below the editor and reddens its border. */
  error?: string | null
}

type JsonCodeEditorProps = JsonCodeEditorOwnProps & AccessibleName

/**
 * A JSON editor with syntax highlighting, built on CodeMirror 6 via
 * `@uiw/react-codemirror` and the official `@codemirror/lang-json` language
 * package. Lazy-loaded client-side and themed from next-themes, mirroring
 * {@link SqlEditor}.
 *
 * This is the text-level counterpart to `JsonEditor`, which takes and returns a
 * parsed object. Prefer this one for form fields, where the value is a string
 * the user is midway through typing and may contain template variables.
 */
export function JsonCodeEditor({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  height,
  rows = DEFAULT_ROWS,
  maxHeight = DEFAULT_MAX_HEIGHT,
  className = "",
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaRequired,
  disabled = false,
  readOnly = false,
  chromeless = false,
  lint = true,
  error,
}: JsonCodeEditorProps) {
  const { resolvedTheme } = useTheme()

  // Held in refs so inline handlers at the call sites do not change identity
  // every render, which would make @uiw reconfigure the whole extension set on
  // each keystroke.
  const onChangeRef = useRef(onChange)
  const onFocusRef = useRef(onFocus)
  const onBlurRef = useRef(onBlur)
  useEffect(() => {
    onChangeRef.current = onChange
    onFocusRef.current = onFocus
    onBlurRef.current = onBlur
  }, [onChange, onFocus, onBlur])

  const handleChange = useCallback((next: string) => {
    onChangeRef.current(next)
  }, [])

  const extensions = useMemo(() => {
    // @uiw spreads unrecognized props onto its wrapper div, which carries no
    // role, so naming attributes have to be put on the content element itself.
    const contentAttributes: Record<string, string> = {}
    if (ariaLabel) {
      contentAttributes["aria-label"] = ariaLabel
    }
    if (ariaLabelledBy) {
      contentAttributes["aria-labelledby"] = ariaLabelledBy
    }
    if (ariaDescribedBy) {
      contentAttributes["aria-describedby"] = ariaDescribedBy
    }
    if (ariaRequired) {
      contentAttributes["aria-required"] = "true"
    }

    const active = [
      json(),
      EditorView.lineWrapping,
      semanticSurfaceTheme,
      EditorView.contentAttributes.of(contentAttributes),
      EditorView.domEventHandlers({
        focus: () => {
          onFocusRef.current?.()
        },
        blur: () => {
          onBlurRef.current?.()
        },
      }),
    ]
    if (lint) {
      active.push(jsonSyntaxLinter)
      if (!chromeless) {
        active.push(lintGutter())
      }
    }
    return active
  }, [
    lint,
    chromeless,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    ariaRequired,
  ])

  // A caller-set `height` is usually "100%", which only resolves if the
  // wrapper between it and the caller's sized container has a height too.
  const fillsContainer = height ? "h-full" : ""

  const setup = useMemo(
    () => ({
      foldGutter: false,
      highlightActiveLine: false,
      lineNumbers: !chromeless,
      highlightActiveLineGutter: !chromeless,
    }),
    [chromeless],
  )

  return (
    <div className={`${fillsContainer} ${className}`}>
      <div
        className={`overflow-hidden ${fillsContainer} ${
          chromeless
            ? ""
            : `rounded border ${error ? "border-coval-danger-border" : "border-coval-border"}`
        } ${disabled ? "opacity-60" : ""}`}
      >
        <CodeMirror
          value={value}
          onChange={handleChange}
          height={height}
          minHeight={
            height
              ? undefined
              : `${rows * LINE_HEIGHT_PX + CONTENT_PADDING_PX}px`
          }
          maxHeight={height ? undefined : maxHeight}
          theme={resolvedTheme === "dark" ? "dark" : "light"}
          extensions={extensions}
          placeholder={placeholder}
          editable={!disabled}
          readOnly={readOnly}
          basicSetup={setup}
        />
      </div>
      {error && <p className="mt-1 text-xs text-coval-danger-text">{error}</p>}
    </div>
  )
}
